/**
 * Desenho do rio — o cenário, sem nada que se mexa por conta própria.
 *
 * Vive aqui, e não dentro da GameScene, porque o MENU também mostra o rio
 * rolando ao fundo. A regra do Wang dual-grid e a da decoração de margem são
 * conhecimento não-óbvio (um tile por VÉRTICE, cantos nas 4 células vizinhas):
 * duplicá-las faria o menu divergir do jogo em silêncio na primeira vez que o
 * tileset mudasse.
 *
 * Nada aqui simula: o desenho depende só de `scrollPx` e de uma função que
 * devolve o span navegável de uma linha. No jogo ela vem da simulação; no menu,
 * direto do gerador.
 */

import Phaser from 'phaser'
import { SCREEN, WORLD, generateChunk, mixSeed, type Chunk, type TerrainSpan } from '@pixelraid/core'
import { WANG_FRAMES } from './wang'

/** Borda navegável de uma linha do mundo. */
export type SpanAtLine = (line: number) => TerrainSpan

/**
 * Fatia a folha Wang em 16 frames nomeados pelo bitmask dos cantos.
 * Idempotente: a textura é global do jogo e várias cenas a preparam.
 */
export function ensureWangFrames(scene: Phaser.Scene): void {
  const texture = scene.textures.get('tileset_river')
  for (let mask = 0; mask < 16; mask++) {
    const frame = WANG_FRAMES[mask]!
    if (texture.has(`w${mask}`) === false) {
      texture.add(`w${mask}`, 0, frame.x, frame.y, frame.w, frame.h)
    }
  }
}

/**
 * Spans direto do gerador, sem simulação — é o que o menu usa.
 *
 * Sem `safe`: os chunks de abertura mansos do início de partida não fazem
 * sentido num pano de fundo que rola para sempre. O cache é podado porque o
 * menu pode ficar aberto indefinidamente.
 */
export function generatorSpans(seed: number, playerCount = 1): SpanAtLine {
  const cache = new Map<number, Chunk>()
  return (line: number): TerrainSpan => {
    const index = (line / WORLD.CHUNK_LINES) | 0
    let chunk = cache.get(index)
    if (chunk === undefined) {
      chunk = generateChunk(seed, index, false, playerCount)
      cache.set(index, chunk)
      if (cache.size > 12) {
        for (const key of cache.keys()) {
          if (cache.size <= 8) break
          if (key !== index) cache.delete(key)
        }
      }
    }
    const i = line - index * WORLD.CHUNK_LINES
    return {
      leftPx: chunk.leftPx[i] ?? 0,
      rightPx: chunk.rightPx[i] ?? SCREEN.WIDTH,
      islandLeftPx: chunk.islandLeftPx[i] ?? 0,
      islandRightPx: chunk.islandRightPx[i] ?? 0,
      safe: chunk.safe,
    }
  }
}

export interface RiverViewOptions {
  /** Seed do mundo — só a decoração de margem depende dela aqui. */
  seed: number
  spanAt: SpanAtLine
  /** Altura visível em px: a área de jogo no jogo, a tela toda no menu. */
  height: number
  /** Profundidade da água e dos tiles; a decoração fica em `depth + 1`. */
  depth?: number
}

/** Água + tiles de margem + decoração. Recicla display objects entre frames. */
export class RiverView {
  private readonly scene: Phaser.Scene
  private readonly seed: number
  private readonly spanAt: SpanAtLine
  private readonly height: number
  private readonly depth: number
  private readonly water: Phaser.GameObjects.TileSprite
  private readonly tiles: Phaser.GameObjects.Image[] = []
  private tilesUsed = 0
  private readonly decos = new Map<string, Phaser.GameObjects.Image[]>()
  private readonly decosUsed = new Map<string, number>()

  constructor(scene: Phaser.Scene, options: RiverViewOptions) {
    this.scene = scene
    this.seed = options.seed
    this.spanAt = options.spanAt
    this.height = options.height
    this.depth = options.depth ?? 0

    ensureWangFrames(scene)
    // Base de água: o tile todo-água (máscara 0) repetido na área visível —
    // um único objeto cobre o fundo; só terra e transições viram tiles.
    this.water = scene.add
      .tileSprite(SCREEN.WIDTH / 2, this.height / 2, SCREEN.WIDTH, this.height, 'tileset_river', 'w0')
      .setDepth(this.depth)
  }

  private tile(): Phaser.GameObjects.Image {
    if (this.tilesUsed < this.tiles.length) {
      const image = this.tiles[this.tilesUsed++]!
      image.setVisible(true)
      return image
    }
    const image = this.scene.add.image(0, 0, 'tileset_river').setDepth(this.depth)
    this.tiles.push(image)
    this.tilesUsed++
    return image
  }

  private deco(key: string): Phaser.GameObjects.Image {
    let pool = this.decos.get(key)
    if (pool === undefined) {
      pool = []
      this.decos.set(key, pool)
    }
    const used = this.decosUsed.get(key) ?? 0
    this.decosUsed.set(key, used + 1)
    if (used < pool.length) {
      const image = pool[used]!
      image.setVisible(true)
      return image
    }
    const image = this.scene.add.image(0, 0, key).setDepth(this.depth + 1)
    pool.push(image)
    return image
  }

  /**
   * Decoração da margem (§6.2 passo 4): árvores e casas em terra, densidade
   * ~15%/linha. Puramente COSMÉTICA — o hash determinístico por (seed, linha)
   * dá estabilidade entre frames sem tocar a simulação.
   */
  private drawDeco(line: number, screenY: number, span: TerrainSpan): void {
    const h = mixSeed(this.seed ^ 0xdec0, line)
    if (h % 100 >= 15) return
    const side = (h >> 4) & 1
    const marginW = side === 0 ? span.leftPx : SCREEN.WIDTH - span.rightPx
    if (marginW < 30) return
    const key = (h >> 8) % 4 === 0 ? 'deco_house' : 'deco_tree'
    const x = side === 0 ? (span.leftPx / 2) | 0 : span.rightPx + ((SCREEN.WIDTH - span.rightPx) / 2) | 0
    this.deco(key).setPosition(x, screenY + 8)
  }

  /** Redesenha o cenário para um deslocamento de rolagem em px. */
  draw(scrollPx: number): void {
    this.tilesUsed = 0
    for (const key of this.decos.keys()) this.decosUsed.set(key, 0)

    this.water.tilePositionY = -scrollPx

    const firstLine = Math.max(0, Math.floor(scrollPx / SCREEN.TILE))
    const lastLine = Math.floor((scrollPx + this.height) / SCREEN.TILE) + 1

    const isLandCell = (col: number, line: number): boolean => {
      // Fora da grade horizontal é terra (margens contínuas).
      if (col < 0 || col >= SCREEN.GRID_W) return true
      const span = this.spanAt(Math.max(0, line))
      const x = col * SCREEN.TILE
      if (x < span.leftPx || x >= span.rightPx) return true
      return span.islandRightPx > span.islandLeftPx && x >= span.islandLeftPx && x < span.islandRightPx
    }

    // Um tile é desenhado POR VÉRTICE da grade de células; seus 4 cantos são
    // as 4 células vizinhas (terra=1). Como as bordas do gerador são alinhadas
    // a tile, cada célula é uniformemente terra ou água — o Wang desenha as
    // transições sozinho, ilhas incluídas.
    for (let line = firstLine; line <= lastLine + 1; line++) {
      const screenY = this.height - (line * SCREEN.TILE - scrollPx)
      if (screenY < -SCREEN.TILE || screenY > this.height + SCREEN.TILE) continue
      for (let v = 0; v <= SCREEN.GRID_W; v++) {
        const nw = isLandCell(v - 1, line) ? 1 : 0
        const ne = isLandCell(v, line) ? 1 : 0
        const sw = isLandCell(v - 1, line - 1) ? 1 : 0
        const se = isLandCell(v, line - 1) ? 1 : 0
        const mask = (nw << 3) | (ne << 2) | (sw << 1) | se
        if (mask === 0) continue // água pura: a base cobre
        const image = this.tile()
        image.setFrame(`w${mask}`)
        image.setPosition(v * SCREEN.TILE, screenY)
      }
    }

    for (let line = firstLine; line <= lastLine; line++) {
      const screenY = this.height - (line * SCREEN.TILE + SCREEN.TILE - scrollPx)
      this.drawDeco(line, screenY, this.spanAt(line))
    }

    for (let i = this.tilesUsed; i < this.tiles.length; i++) this.tiles[i]!.setVisible(false)
    for (const [key, pool] of this.decos) {
      const used = this.decosUsed.get(key) ?? 0
      for (let i = used; i < pool.length; i++) pool[i]!.setVisible(false)
    }
  }
}
